import React, { useMemo } from "react";
import { FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack } from "@chakra-ui/layout";
import * as Yup from "yup";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { FormStack } from "../components/FormStack/FormStack";
import { useAdminApiRequest } from "../hooks/useApiRequest";

export interface GeoLocationGroupDetailFormValues {
  displayName?: string;
  geoLocationIds?: string[];
}

interface GeoLocationGroupDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  geoLocationGroup?: CherryHubAdminApi.GeoLocationGroup | null;
  onCancel: () => void;
  onSuccess: (geoLocationGroup: CherryHubAdminApi.GeoLocationGroup) => void;
  onFailure: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  submitForm,
  onCancel,
  businessId,
}: GeoLocationGroupDetailFormProps &
  FormikProps<GeoLocationGroupDetailFormValues>) => {
  const { data: geoLocations, isLoading: geoLocationsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        businessId ? apiClient.getGeoLocations(businessId) : null,
      [businessId]
    );

  const geoLocationOptions = useMemo(
    () =>
      (geoLocations ?? [])
        .filter((geoLocation) => geoLocation.Meta?.Id)
        .map((geoLocation) => ({
          label: geoLocation.DisplayName ?? geoLocation.GeoLocationId,
          value: geoLocation.Meta!.Id!,
        })),
    [geoLocations]
  );

  return (
    <Box width="100%" maxW="800px" alignSelf="flex-start">
      <FormStack>
        <TextField horizontal label="Display Name" name="displayName" />
        <ChakraReactSelectField
          horizontal
          label="Geo Locations"
          name="geoLocationIds"
          options={geoLocationOptions}
          isLoading={geoLocationsLoading}
          selectedOptionColor="blue"
          isMulti
        />

        <HStack width="100%" justifyContent="center" spacing="3" pt="8">
          <Button
            colorScheme="cherryButton"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={submitForm}
          >
            Save
          </Button>
          <Button disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </HStack>
      </FormStack>
    </Box>
  );
};

export const GeoLocationGroupDetailForm = withFormik<
  GeoLocationGroupDetailFormProps,
  GeoLocationGroupDetailFormValues
>({
  mapPropsToValues: ({ geoLocationGroup }) => ({
    displayName: geoLocationGroup?.DisplayName ?? "",
    geoLocationIds: geoLocationGroup?.GeoLocationIds ?? [],
  }),

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: false,

  validationSchema: Yup.object().shape({
    displayName: Yup.string().trim().required("Display Name is required."),
    geoLocationIds: Yup.array().of(Yup.string().required()).notRequired(),
  }),

  handleSubmit: async (values, { props }) => {
    const update: CherryHubAdminApi.GeoLocationGroupUpdate = {
      DisplayName: values.displayName!.trim(),
      GeoLocationIds: values.geoLocationIds ?? [],
    };

    const result = props.geoLocationGroup?.Meta?.Id
      ? await props.apiClient.updateGeoLocationGroup(
          props.businessId,
          props.geoLocationGroup.Meta.Id,
          update
        )
      : await props.apiClient.createGeoLocationGroup(props.businessId, update);

    if (result.ok) {
      props.onSuccess(result.data);
    } else {
      props.onFailure(result.message);
    }
  },
})(InnerForm);
